import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Alert,
  CircularProgress,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
  Typography,
  Card,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  WaterDrop as GroupIcon,
} from '@mui/icons-material';
import productsService, { type EvaporationDefault } from '../services/products';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import EmptyState from '../components/EmptyState';
import { HEADER_CELL_SX, DetailItem } from '../components/ListPageShared';

const COLUMN_COUNT = 4;

const EvaporationLimits: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canEdit = hasPermission(user, 'evap_limits.edit');
  const canCreate = hasPermission(user, 'evap_limits.create_group');

  const [items, setItems] = useState<EvaporationDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [deleteTarget, setDeleteTarget] = useState<EvaporationDefault | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await productsService.listEvaporationDefaults();
      setItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load evaporation limit groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filteredItems = useMemo(() => {
    if (!searchFilter.trim()) return items;
    const q = searchFilter.toLowerCase();
    return items.filter((d) =>
      (d.default_id?.toLowerCase().includes(q)) ||
      (d.description?.toLowerCase().includes(q)) ||
      (d.rev_no?.toLowerCase().includes(q))
    );
  }, [items, searchFilter]);

  const pagedItems = filteredItems.slice(page * pageSize, page * pageSize + pageSize);

  useEffect(() => {
    setPage(0);
  }, [searchFilter]);

  // Matches the Mix Designs list: "12 Jul 2026 · username"
  const formatCreated = (row: EvaporationDefault): string => {
    if (!row.date_create) return '—';
    const iso = row.date_create;
    const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
    const d = new Date(hasZone ? iso : `${iso}Z`);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
    return row.create_by ? `${date} · ${row.create_by}` : date;
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productsService.deleteEvaporationDefault(deleteTarget.id);
      setSnackbar({
        msg: t('evaporationLimit.deleted', 'Evaporation limit group deleted'),
        severity: 'success',
      });
      setDeleteTarget(null);
      fetchItems();
    } catch (e: any) {
      setSnackbar({
        msg: e.response?.data?.detail || 'Delete failed',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  // Evaporation groups have no active flag, so every row gets the primary edge
  const edgeColor = theme.palette.primary.main;

  const renderExpandedContent = (d: EvaporationDefault) => (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, rowGap: 2 }}>
        <DetailItem label={t('evaporationLimit.defaultId', 'Group ID')}>{d.default_id || '—'}</DetailItem>
        <DetailItem label={t('evaporationLimit.description', 'Description')}>{d.description || '—'}</DetailItem>
        <DetailItem label={t('evaporationLimit.revNo', 'Rev No')}>
          {d.rev_no ? <Box component="span" sx={{ fontFamily: 'monospace' }}>{d.rev_no}</Box> : '—'}
        </DetailItem>
        <DetailItem label={t('evaporationLimit.dateCreated', 'Date Created')}>{formatCreated(d)}</DetailItem>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewIcon />}
          onClick={() => navigate(`/evaporation-limits/${d.id}`)}
          sx={{ textTransform: 'none' }}
        >
          {t('users.viewDetails', 'View Details')}
        </Button>
        {canEdit && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/evaporation-limits/${d.id}/edit`)}
              sx={{ textTransform: 'none' }}
            >
              {t('common.edit', 'Edit')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteTarget(d)}
              sx={{ textTransform: 'none' }}
            >
              {t('common.delete', 'Delete')}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  const emptyState = (
    <EmptyState
      title={t('evaporationLimit.noItems', 'No evaporation limit groups found')}
      description={t('evaporationLimit.noItemsDescription', 'Create a new evaporation limit group to get started.')}
      icon={<SearchIcon />}
      actionLabel={canCreate ? t('evaporationLimit.addNew', 'Add Evaporation Limit Group') : undefined}
      onAction={canCreate ? () => navigate('/evaporation-limits/new') : undefined}
    />
  );

  const pagination = (
    <TablePagination
      rowsPerPageOptions={[10, 20, 50, 100]}
      component="div"
      count={filteredItems.length}
      rowsPerPage={pageSize}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(0);
      }}
      labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage')}
      sx={isMobile ? { '& .MuiTablePagination-selectLabel': { display: 'none' } } : undefined}
    />
  );

  if (loading && items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary = searchFilter
    ? `Showing ${filteredItems.length} of ${items.length} (filtered)`
    : `Total: ${items.length}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          variant="outlined"
          placeholder={t('evaporationLimit.searchPlaceholder', 'Search by Group ID, description...')}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: { xs: '100%', sm: 400 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t('common.refresh')}>
          <span>
            <IconButton onClick={fetchItems} disabled={loading} aria-label={t('common.refresh')}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        {canCreate && (
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => navigate('/evaporation-limits/new')}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
          >
            {t('evaporationLimit.addNew', 'Add Evaporation Limit Group')}
          </Button>
        )}
      </Box>

      {isMobile ? (
        /* Mobile card list */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredItems.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            pagedItems.map((d) => {
              const expanded = expandedId === d.id;
              return (
                <Card key={d.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${edgeColor}` }}>
                  <Box onClick={() => setExpandedId(expanded ? null : d.id)} sx={{ px: 2, py: 1.5, cursor: 'pointer' }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {d.default_id || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }} noWrap>
                      {d.description || '—'}
                    </Typography>
                    {d.rev_no && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        Rev {d.rev_no}
                      </Typography>
                    )}
                  </Box>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                      {renderExpandedContent(d)}
                    </Box>
                  </Collapse>
                </Card>
              );
            })
          )}
          {pagination}
        </Box>
      ) : (
        /* Desktop table with expandable rows */
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 40, p: 0.5 }} />
                <TableCell sx={HEADER_CELL_SX}>{t('evaporationLimit.group', 'Limit Group')}</TableCell>
                <TableCell sx={HEADER_CELL_SX} align="center">{t('evaporationLimit.revNo', 'Rev No')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('evaporationLimit.dateCreated', 'Date Created')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                    {loading ? <Typography color="text.secondary">{t('common.loading')}</Typography> : emptyState}
                  </TableCell>
                </TableRow>
              ) : (
                pagedItems.map((d) => {
                  const expanded = expandedId === d.id;
                  return (
                    <React.Fragment key={d.id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedId(expanded ? null : d.id)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: expanded ? 'action.selected' : undefined,
                          '& > td:first-of-type': { borderLeft: `4px solid ${edgeColor}` },
                        }}
                      >
                        <TableCell sx={{ p: 0.5 }} align="center">
                          <IconButton size="small" aria-label={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}>
                            {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <GroupIcon sx={{ fontSize: 28, color: 'text.secondary', flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {d.default_id || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 420 }}>
                                {d.description || '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: d.rev_no ? 'text.primary' : 'text.disabled' }}>
                            {d.rev_no || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {formatCreated(d)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 3, py: 2, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
                              {renderExpandedContent(d)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
            </Typography>
            {pagination}
          </Box>
        </TableContainer>
      )}

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>
          {t('evaporationLimit.deleteConfirmTitle', 'Delete evaporation limit group?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('evaporationLimit.deleteConfirmBody', {
              defaultValue: 'Delete "{{label}}"? This cannot be undone.',
              label: deleteTarget?.default_id || deleteTarget?.description || deleteTarget?.id,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting
              ? t('common.deleting', 'Deleting...')
              : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvaporationLimits;
